import { Button, Divider, Flex, Paper, Stack } from "@mantine/core";
import { purchaseOrderApi } from "apis/purchaseOrderApi";
import { Loading } from "components";
import { ErrorStatusType } from "constants/types/common";
import {
  PurchaseOrderCreateBodyType,
  PurchaseOrderResponseType,
  PurchaseOrderUpdateBodyType,
} from "constants/types/purchaseOrder";
import { FormikProvider, useFormik } from "formik";
import { FORM_IDS } from "pages/PurchaseOrder/config";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PURCHASE_ORDER_PAGE } from "routes/route.constant";
import { useNotification } from "utils/hooks";
import { useListProduct } from "utils/hooks/request/useProducts";
import {
  POCreateFormValues,
  createValidationSchema,
  initialValues,
  generateRowKey,
} from "../../config";
import PurchaseOrderGeneralFields from "../PurchaseOrderGeneralFields/PurchaseOrderGeneralFields";
import PurchaseOrderItemsFieldArray from "../PurchaseOrderItemsFieldArray/PurchaseOrderItemsFieldArray";
import PurchaseOrderPartnerFields from "../PurchaseOrderPartnerFields/PurchaseOrderPartnerFields";

type PurchaseOrderCreateFormProps = {
  mode?: "create" | "edit";
  purchaseOrder?: PurchaseOrderResponseType;
  onSaved?: () => void;
};

const PurchaseOrderCreateForm: FC<PurchaseOrderCreateFormProps> = ({
  mode = "create",
  purchaseOrder,
  onSaved,
}) => {
  const navigate = useNavigate();
  const { notification } = useNotification();
  const { products } = useListProduct();
  const [isLoading, setIsLoading] = useState(false);

  const formInitialValues = useMemo<POCreateFormValues>(() => {
    if (!purchaseOrder) return initialValues;
    return {
      code: purchaseOrder.code ?? "",
      // Sửa đơn thì nạp lại đúng ngày của đơn, không phải ngày hôm nay
      createdAt: purchaseOrder.createdAt ?? initialValues.createdAt,
      warehouseId: purchaseOrder.warehouseId ?? "",
      notes: purchaseOrder.notes ?? "",
      partner: {
        id: purchaseOrder.partnerId ?? "",
        name: purchaseOrder.partner?.name ?? "",
        phone: purchaseOrder.partner?.phone ?? "",
        taxCode: purchaseOrder.partner?.taxCode ?? "",
        contactPersonId: purchaseOrder.partner?.contactPersonId ?? "",
        regionId: purchaseOrder.partner?.regionId ?? "",
        notes: purchaseOrder.partner?.notes ?? "",
        isActive: purchaseOrder.partner?.isActive ?? true,
      },
      items:
        purchaseOrder.items && purchaseOrder.items.length > 0
          ? purchaseOrder.items.map((item) => ({
              rowKey: generateRowKey(),
              productId: item.productId,
              productName: item.productName,
              productSpec: item.productSpec ?? "",
              productUnit: item.productUnit,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
            }))
          : initialValues.items,
    };
  }, [purchaseOrder]);

  const formik = useFormik<POCreateFormValues>({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema: createValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // Cùng lý do như đơn bán: ba field UUID phải bỏ hẳn khi rỗng
        const {
          id,
          notes: partnerNotes,
          contactPersonId,
          regionId,
          ...partnerRest
        } = values.partner;

        const payload: PurchaseOrderCreateBodyType | PurchaseOrderUpdateBodyType = {
          code: values.code,
          createdAt: values.createdAt,
          warehouseId: values.warehouseId,
          notes: values.notes || undefined,
          partner: {
            ...(id ? { id } : {}),
            ...partnerRest,
            notes: partnerNotes || undefined,
            ...(regionId ? { regionId } : {}),
            ...(contactPersonId ? { contactPersonId } : {}),
          },
          items: values.items.map(
            ({ rowKey: _rowKey, productSpec, ...item }) => ({
              ...item,
              ...(productSpec ? { productSpec } : {}),
            }),
          ),
        };

        const res =
          mode === "edit" && purchaseOrder
            ? await purchaseOrderApi.update(purchaseOrder.id, payload)
            : await purchaseOrderApi.create(payload as PurchaseOrderCreateBodyType);
        if (res.data.success) {
          notification.success({
            title:
              mode === "edit"
                ? "Cập nhật đơn mua hàng thành công"
                : "Tạo đơn mua hàng thành công",
          });
          onSaved?.();
          if (mode === "create") navigate(PURCHASE_ORDER_PAGE);
        }
      } catch (error: ErrorStatusType | any) {
        notification.error({
          title:
            mode === "edit"
              ? "Cập nhật đơn mua hàng thất bại"
              : "Tạo đơn mua hàng thất bại",
          message: error?.error?.message,
        });
      }
      setIsLoading(false);
    },
  });

  return (
    <Loading variant="overlay" visible={isLoading}>
      <FormikProvider value={formik}>
        <form id={FORM_IDS.CREATE} onSubmit={formik.handleSubmit}>
          <Stack gap="md">
            <Paper p="md" withBorder>
              <PurchaseOrderGeneralFields formik={formik} />
            </Paper>

            <Paper p="md" withBorder>
              <PurchaseOrderPartnerFields formik={formik} />
            </Paper>

            <Paper p="md" withBorder>
              <PurchaseOrderItemsFieldArray formik={formik} products={products ?? []} />
            </Paper>

            <Divider />

            <Flex justify={"center"}>
              <Button
                type="submit"
                form={FORM_IDS.CREATE}
                w={250}
                loading={isLoading}
              >
                {mode === "edit" ? "Lưu thay đổi" : "Tạo đơn mua hàng"}
              </Button>
            </Flex>
          </Stack>
        </form>
      </FormikProvider>
    </Loading>
  );
};

export default PurchaseOrderCreateForm;
